import { cloneElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Configuration for an instance of useTeleport().
 */

const DefaultOptions = {};

/**
 * Create a Teleporting React Element. Teleportation can be used to move a component around the
 * document without unmounting it, thus preserving inputs, hooks, and component state.
 *
 * The intended use case for teleportation is expensive and stateful components that have "pop-out"
 * or "expand" behavior and need to move elsewhere in the DOM temporarily. Only the DOM nodes of
 * the component move; the React tree does not change.
 *
 * The supplied `innerComponent` will be wrapped in a plain <div/> called the teleportContainer.
 * The teleportContainer is moved around in the DOM, with your React elements inside it.
 * If you need a different kind of wrapper (e.g., a <li> instead of <div>), override the
 * `elementConstructor()` option and return any valid HTMLElement to be used instead.
 *
 * If you render the portal from useTeleport() in a loop or with an array, be sure to set the
 * `key` option to something unique.
 *
 * A few basic rules of teleportation:
 *
 *  1. A component can only exist in one place at a time. Teleporting to a new
 *     location will remove the component from its last location.
 *  2. When an entity teleports, it *replaces* its target. The internal state of
 *     useTeleport() will remember what was replaced and restore it when needed.
 *  3. You can only teleport to real DOM nodes. If you use a React component as a
 *     teleportation target, make sure it forwards its `ref` to a concrete DOM node.
 *  4. Upon initialization, the teleported component only lives in memory. You must
 *     perform an initial teleportation to the component's home position before it
 *     will appear.
 *  5. The home positon and any remote positions can be at any place in the DOM or
 *     the React tree, but it's up to you to pass Refs around and manage lifecycles
 *     correctly.
 *  6. It doesn't matter where the `portal` is rendered, only that it stays in the
 *     React tree somewhere for as long as the useTeleport hook is active.
 *
 */
export function useTeleport(innerComponent, {
  key,
  elementConstructor
} = DefaultOptions) {
  const teleportContainer = useMemo(() => {
    if (elementConstructor) return elementConstructor();
    const e = document.createElement('div');
    return e;
  }, [elementConstructor]);
  const portal = useMemo(() => /*#__PURE__*/createPortal( /*#__PURE__*/cloneElement(innerComponent), teleportContainer, key), [innerComponent, teleportContainer, key]);
  const replacedNodeRef = useRef(null);
  const relocate = useCallback(
  // `null` is allowed as an argument, which makes the component teleport
  // to the invisible memory node instead of the document.
  target => {
    // Check if we're already teleported into the given target
    if (replacedNodeRef.current === target) return;

    /*
    Check that the node is a concrete DOM node; we can't teleport into React instances.
     Refs that are defined simply as `useRef(null)` will be typed as React.MutableRefObject<null>.
    When used in `relocate(ref.current)`, then `ref.current` is typed as `null`, which is valid.
    At runtime, however, the ref may be assigned to a React.Component instance instead, and
    that will cause an error. Check for that case here and warn about it.
     If your refs are correctly typed in their definition e.g. `useRef<HTMLDivElement>(null)`,
    then TypeScript will catch the problem. Otherwise, this is an easy footgun.
    */
    if (target && !(target instanceof Node)) {
      throw new Error(`useTeleport -- Failed to teleport element. The chosen target is not a DOM Node.`);
    }

    // If there is any replacedNode, put it back first
    if (replacedNodeRef.current) {
      teleportContainer.parentElement.replaceChild(replacedNodeRef.current, teleportContainer);
      replacedNodeRef.current = null;
    }

    // Teleport into the given target location
    if (target) {
      target.parentElement.replaceChild(teleportContainer, target);
      replacedNodeRef.current = target;
    }
  }, [teleportContainer]);

  // If this hook un-mounts without doing proper cleanup, restore the DOM
  // to its original state before we unmount.
  useEffect(() => {
    return () => {
      if (replacedNodeRef.current) {
        relocate(null);
      }
    };
  }, [relocate]);
  return [portal, relocate];
}